import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, Shield, DollarSign, FileText, CheckCircle, 
  Wallet, CreditCard, ArrowRight, ArrowLeft, HelpCircle,
  Users, Lock, TrendingUp, AlertTriangle, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const InvestorGuide = () => {
  const steps = [
    {
      number: 1,
      title: "Create an Account",
      description: "Sign up with your email and set a secure password.",
      icon: Users,
      link: "/signup",
      linkText: "Sign Up Now"
    },
    {
      number: 2,
      title: "Complete Accreditation",
      description: "Verify your accredited investor status to participate in the platform.",
      icon: Shield,
      link: "/onboarding",
      linkText: "Start Verification"
    },
    {
      number: 3,
      title: "Connect Your Wallet",
      description: "Link your Ethereum wallet (MetaMask) to interact with the platform.",
      icon: Wallet,
      link: "/dashboard",
      linkText: "Go to Dashboard"
    },
    {
      number: 4,
      title: "Receive BETH Tokens",
      description: "Receive BETH utility tokens for use as collateral in credit enhancement.",
      icon: DollarSign,
      link: "/credit",
      linkText: "View Credit Options"
    },
    {
      number: 5,
      title: "Use Credit Enhancement",
      description: "Use your BETH as conditional collateral for debt settlement with lenders.",
      icon: CreditCard,
      link: "/credit-enhancement",
      linkText: "Credit Enhancement"
    }
  ];

  const features = [
    {
      title: "Credit Enhancement",
      description: "Use BETH tokens as conditional collateral to enhance creditworthiness for securities finance and structured transactions.",
      icon: TrendingUp
    },
    {
      title: "Private Accord & Satisfaction (PAS)",
      description: "Facilitate transaction settlements with BETH-backed guarantees for repo, swaps, and structured credit arrangements.",
      icon: FileText
    },
    {
      title: "Non-Custodial",
      description: "You always maintain control of your assets. The platform never takes custody of your tokens.",
      icon: Lock
    },
    {
      title: "Compliance First",
      description: "Built for accredited investors and qualified institutional participants with full regulatory compliance.",
      icon: Shield
    }
  ];

  const faqs = [
    {
      question: "What is BETH token?",
      answer: "BETH (Bethereum) is a utility token used as conditional collateral within the platform. It is NOT a security and has no investment value outside of its utility function."
    },
    {
      question: "How does Credit Enhancement work?",
      answer: "You can use BETH tokens as conditional collateral when structuring transactions with counterparties. BETH serves as credit support for securities finance, repo, swaps, and structured credit arrangements."
    },
    {
      question: "Who can participate?",
      answer: "Only accredited investors and qualified institutional participants who complete the verification process can participate in the platform. This ensures compliance with securities regulations."
    },
    {
      question: "How do I get BETH tokens?",
      answer: "BETH tokens are distributed to qualified participants through the platform. Contact the treasury or your account manager for token allocation."
    },
    {
      question: "What transactions are supported?",
      answer: "The platform supports securities finance, repo transactions, swaps, structured credit, prime brokerage, margin lending, and collateral management arrangements."
    },
    {
      question: "Is my wallet secure?",
      answer: "Yes. The platform is non-custodial, meaning you always control your private keys. We never have access to move your funds without your explicit signature."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="investor-guide-page">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Home
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-[#b8860b]" />
                Investor Guide
              </h1>
              <p className="text-gray-400 text-sm">Learn how to use the Bethereum platform</p>
            </div>
          </div>
          <Link to="/signup">
            <Button className="bg-[#b8860b] hover:bg-[#9a7209] text-white">
              Get Started
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Introduction */}
        <div className="mb-12">
          <Card className="bg-gradient-to-br from-[#0d1d35] to-[#1a2d4a] border-[#b8860b]/30">
            <CardContent className="py-8">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Welcome to Bethereum Private Credit
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  A private wealth management platform that enables accredited investors to leverage 
                  BETH utility tokens for credit enhancement and debt settlement solutions.
                </p>
                <div className="flex justify-center gap-4">
                  <Link to="/signup">
                    <Button size="lg" className="bg-[#b8860b] hover:bg-[#9a7209]">
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/compliance">
                    <Button size="lg" variant="outline" className="border-gray-600 text-white hover:bg-gray-800">
                      View Compliance
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Getting Started Steps */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-[#b8860b]" />
            Getting Started
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step) => (
              <Card key={step.number} className="bg-[#0d1d35] border-[#1a2d4a] hover:border-[#b8860b]/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                      <span className="text-[#b8860b] font-bold">{step.number}</span>
                    </div>
                    <step.icon className="w-6 h-6 text-[#b8860b]" />
                  </div>
                  <CardTitle className="text-white mt-3">{step.title}</CardTitle>
                  <CardDescription className="text-gray-400">{step.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={step.link}>
                    <Button variant="outline" className="w-full border-[#b8860b]/50 text-[#b8860b] hover:bg-[#b8860b]/10">
                      {step.linkText}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Platform Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#b8860b]" />
            Platform Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#b8860b]/20 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#b8860b]" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-2">{feature.title}</h3>
                      <p className="text-gray-400 text-sm">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Supported Transaction Types */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#b8860b]" />
            Supported Transaction Types
          </h2>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Securities Finance', 'Repo Transactions', 'Swaps', 'Structured Credit', 'Prime Brokerage', 'Margin Lending', 'Collateral Management', 'Credit Facilities'].map((type) => (
                  <div key={type} className="bg-[#1a2d4a]/50 p-4 rounded-lg text-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-white font-medium">{type}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQs */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-[#b8860b]" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardContent className="pt-6">
                  <h3 className="text-white font-bold mb-2">{faq.question}</h3>
                  <p className="text-gray-400">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <Card className="bg-yellow-900/20 border-yellow-500/30">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
              <div>
                <h3 className="text-white font-bold mb-2">Important Disclosures</h3>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li>• BETH is a utility token, NOT a security or investment product.</li>
                  <li>• This platform is for accredited investors only.</li>
                  <li>• Past performance does not guarantee future results.</li>
                  <li>• Always consult with financial and legal advisors before participating.</li>
                  <li>• Read all compliance documents before using the platform.</li>
                </ul>
                <Link to="/compliance" className="inline-flex items-center gap-2 text-[#b8860b] hover:underline mt-4">
                  View Full Compliance Documentation <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">Need help? Contact our support team.</p>
          <div className="flex justify-center gap-4">
            <a href={`https://etherscan.io/token/0x6822824c5349bf005e353ec5dda3fa34595ea811`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800">
                <ExternalLink className="w-4 h-4 mr-2" />
                View BETH on Etherscan
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorGuide;
